import React, { useEffect, useState } from 'react';
import NextNProgress from 'nextjs-progressbar';
import { Footer } from './footer/footer';
import { AppLayout, AppContent } from './layout.styles';
import NavDrawer from 'common/components/layout/nav-drawer/nav-drawer';
import { Header } from './header/header';
import { useRouter } from 'next/router';
import { useTheme } from 'styled-components';
import { AlertContainer } from '@sapiens/insured-component-library';
import getConfig from 'next/config';
import { checkPoliciesStatus } from 'common/utils/helpers/policies.helpers';

declare global {
  interface Window {
    unSavedChangesInJourney: (...args: unknown[]) => void;
    unSavedChangesInFnol: (...args: unknown[]) => void;
    bannerController: (...args: unknown[]) => void;
    getPoliciesStatus: () => void;
  }
}

export const Layout: React.FC = ({ children }) => {
  const router = useRouter();
  const theme = useTheme();
  const [unSavedChangesExistInJourney, setUnSavedChangesExistInJourney] = useState(0);
  const [unSavedChangesExistInFnol, setUnSavedChangesExistInFnol] = useState(0);
  const [onBoardedUser, setOnBoardedUser] = useState(true);
  const [bannerEnabled, setBannerEnabled] = useState(true);

  const { publicRuntimeConfig } = getConfig();

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      if (typeof window !== 'undefined') {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'auto',
        });
      }
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    checkPoliciesStatus(setOnBoardedUser);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.unSavedChangesInJourney = function unSavedChangesInJourney(args) {
      if (args !== 'undefined' && (args as string[]).length > 0) {
        if (Number((args as string[])[0]) === 3) {
          window.location.replace(`${publicRuntimeConfig.hostUrl}/quotes`);
        }

        setUnSavedChangesExistInJourney(Number((args as string[])[0]));
      }
    };

    window.unSavedChangesInFnol = function unSavedChangesInFnol(args) {
      if (args !== 'undefined' && (args as string[]).length > 0) {
        if (Number((args as string[])[0]) === 3) {
          window.location.replace(`${publicRuntimeConfig.hostUrl}/quotes`);
        }

        setUnSavedChangesExistInFnol(Number((args as string[])[0]));
      }
    };

    window.bannerController = function bannerController(args) {
      if (args !== 'undefined' && (args as string[]).length > 0) {
        const command = String((args as string[])[0]).toLowerCase();
        setBannerEnabled(command !== 'disable');
      }
    };

    window.getPoliciesStatus = () => checkPoliciesStatus(setOnBoardedUser);
  }, [publicRuntimeConfig.hostUrl]);

  useEffect(() => {
    if (typeof window === 'undefined' || !bannerEnabled) {
      return;
    }

    let rafId = 0;
    let retryTimer: number | undefined;
    let retries = 0;

    const findBanner = (): HTMLElement | null => document.querySelector<HTMLElement>('.price-breakup-section-cs');

    const findGreenTag = (banner?: HTMLElement | null): HTMLElement | null =>
      banner?.querySelector<HTMLElement>('.price-breakup-green-tag') ||
      document.querySelector<HTMLElement>('.price-breakup-green-tag');

    const findFooter = (): HTMLElement | null =>
      document.querySelector<HTMLElement>('#lkf-footer-container') || document.querySelector<HTMLElement>('footer');

    // stable horizontal anchor from content, not footer
    const findAnchor = (banner: HTMLElement): HTMLElement =>
      (banner.closest('.page-form-background-cs') as HTMLElement) ||
      (banner.closest('.ib-column') as HTMLElement) ||
      (banner.closest('.MuiGrid-root') as HTMLElement) ||
      (banner.parentElement as HTMLElement) ||
      banner;

    const findReserveContainer = (banner: HTMLElement): HTMLElement | null =>
      (banner.closest('.page-form-background-cs') as HTMLElement) ||
      (banner.closest('.cs-section-container') as HTMLElement) ||
      banner.parentElement;

    const cleanupInlineStyles = () => {
      const banner = findBanner();
      const greenTag = findGreenTag(banner);

      if (banner) {
        banner.style.bottom = '0px';
        banner.style.left = '';
        banner.style.width = '';
        banner.style.maxWidth = '';
        banner.style.right = '';
        banner.style.boxSizing = '';
      }

      if (greenTag) {
        greenTag.style.display = '';
        greenTag.style.width = '';
        greenTag.style.maxWidth = '';
        greenTag.style.margin = '';
        greenTag.style.boxSizing = '';
      }
    };

    const updateBannerPosition = () => {
      const banner = findBanner();
      if (!banner) {
        return;
      }

      const greenTag = findGreenTag(banner);
      const footer = findFooter();
      const reserveContainer = findReserveContainer(banner);

      // use footer width when footer exists, otherwise use a stable page container
      const pageAnchor =
        (document.querySelector('.layoutstyles__AppContent-sc-11slkm7-1') as HTMLElement) ||
        (document.querySelector('main') as HTMLElement) ||
        findAnchor(banner);

      const anchorRect = (footer || pageAnchor).getBoundingClientRect();
      const bannerRect = banner.getBoundingClientRect();

      const bannerHeight = Math.ceil(greenTag?.getBoundingClientRect().height || bannerRect.height || 0);

      // outer fixed section
      banner.style.left = `${Math.round(anchorRect.left)}px`;
      banner.style.width = `${Math.round(anchorRect.width)}px`;
      banner.style.maxWidth = 'none';
      banner.style.right = 'auto';
      banner.style.margin = '0';
      banner.style.padding = '0';
      banner.style.boxSizing = 'border-box';

      // visible green strip
      if (greenTag) {
        greenTag.style.display = 'block';
        greenTag.style.width = '100%';
        greenTag.style.maxWidth = 'none';
        greenTag.style.margin = '0';
        greenTag.style.padding = '16px';
        greenTag.style.boxSizing = 'border-box';
      }

      if (reserveContainer instanceof HTMLElement) {
        reserveContainer.style.marginBottom = `${bannerHeight}px`;
      }

      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const overlap = window.innerHeight - footerRect.top;
        banner.style.bottom = overlap > 0 ? `${Math.ceil(overlap)}px` : '0px';
      } else {
        banner.style.bottom = '0px';
      }
    };

    const scheduleUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateBannerPosition);
    };

    const waitForDomThenStart = () => {
      const banner = findBanner();
      if (banner) {
        scheduleUpdate();
        return;
      }

      if (retries < 30) {
        retries += 1;
        retryTimer = window.setTimeout(waitForDomThenStart, 200);
      }
    };

    waitForDomThenStart();

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('load', scheduleUpdate);

    return () => {
      cancelAnimationFrame(rafId);
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }

      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('load', scheduleUpdate);

      cleanupInlineStyles();
    };
  }, [bannerEnabled]);
  return (
    <AppLayout>
      <NextNProgress color={theme.palette.product.F300} options={{ easing: 'ease-in-out', showSpinner: false }} />
      <Header
        unSavedChangesExistInJourney={unSavedChangesExistInJourney}
        unSavedChangesExistInFnol={unSavedChangesExistInFnol}
        onBoardedUser={onBoardedUser}
      />
      <NavDrawer
        unSavedChangesExistInJourney={unSavedChangesExistInJourney}
        unSavedChangesExistInFnol={unSavedChangesExistInFnol}
        onBoardedUser={onBoardedUser}
      />
      <AppContent>{children}</AppContent>
      <AlertContainer />
      <Footer />
    </AppLayout>
  );
};
